/**
 * Formatting that has to agree across pages.
 *
 * Anything here exists because two screens were showing the same value
 * differently, which in an admin is not a cosmetic problem: somebody reads one
 * screen, remembers a number, and then cannot find it on the other.
 */

/**
 * A participant's share of a booking.
 *
 * The **same** stored column, `ParticipantsIndexAsDecimal`, reaches the admin as
 * two different types. `/admin/participants/search` wraps it in a `Percentage`
 * object (`{ AsDecimal, AsPercentage }`); `/admin/purchases/…` sends the bare
 * decimal. So the participants list showed "50%" and the purchase detail showed
 * "0.5" for one and the same participant, and nothing on either screen said they
 * were the same field.
 *
 * A percentage is the right reading. In production the value is a unit fraction
 * — 0.5, 0.25, 1/6, 1/7, 1/8 — which is a share of the booking, not a count of
 * anything. Both shapes are accepted here and both come out as a percentage.
 *
 * One decimal place, and only when it needs one: 50% rather than 50.0%, but
 * 16.7% rather than 17%, because a sixth rounded to a fifth is a different
 * split.
 */
export const participantShare = value => {
  if (value === null || value === undefined || value === '') return null;

  const decimal = typeof value === 'object'
    ? (typeof value.AsDecimal === 'number'
      ? value.AsDecimal
      : (typeof value.AsPercentage === 'number' ? value.AsPercentage / 100 : null))
    : Number(value);

  if (decimal === null || !Number.isFinite(decimal)) return null;

  const percent = decimal * 100;
  const rounded = Math.round(percent * 10) / 10;

  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

/**
 * A span of time, in the coarsest unit that is still useful.
 *
 * One set of thresholds for the whole tree, because two screens disagreeing
 * about whether something is "72 h" or "3 days" is the kind of difference that
 * makes a reader check which one is right instead of acting on either.
 *
 * Minutes under an hour, hours under two days, days above. Never zero: something
 * that happened forty seconds ago has still happened.
 */
const spanOf = milliseconds => {
  const minutes = Math.round(milliseconds / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;

  return `${Math.round(hours / 24)} days`;
};

/**
 * Parses the API's timestamps.
 *
 * The `Z` is deliberately **not** appended. These are CET wall-clock strings
 * with no zone on them, and reading them as UTC would shift every result by an
 * hour or two — which on an "oldest first" panel means a fresh item reading as
 * already stale, and on an expiry means a live code reading as dead.
 */
const parse = value => {
  if (!value) return null;
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * How long something has been waiting.
 *
 * The "needs attention first" panel is sorted by exactly this and used to print
 * a timestamp instead — the raw fact rather than the reading.
 */
export const waitingFor = (value, now = Date.now()) => {
  const then = parse(value);
  if (!then) return null;

  const elapsed = now - then.getTime();
  return elapsed < 0 ? null : spanOf(elapsed);
};

/**
 * How long until something expires, or how long since it did.
 *
 * Written for the SALTO master codes, where the difference between the two is
 * the difference between a code that opens a door and one that does not.
 */
export const validity = (value, now = Date.now()) => {
  const then = parse(value);
  if (!then) return null;

  const remaining = then.getTime() - now;
  return remaining < 0
    ? { expired: true, text: `expired ${spanOf(-remaining)} ago` }
    : { expired: false, text: `valid ${spanOf(remaining)} more` };
};
